import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Crown, Mail, PauseCircle, PlayCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { listAdminUsers, promoteUserToAdmin, type AdminUserItem } from '@/lib/api/admin'

type RoleFilter = 'all' | 'viewer' | 'creator' | 'admin'
type StatusFilter = 'all' | 'active' | 'suspended'

interface AdminUser extends AdminUserItem {
  status?: 'active' | 'suspended'
}

export function AdminUsers() {
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [lastAction, setLastAction] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const items = await listAdminUsers()
        if (cancelled) return
        setUsers(
          items.map((u) => ({
            ...u,
            status: 'active',
          })),
        )
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load users')
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredUsers = useMemo(() => {
    const byFilter = users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false
      if (statusFilter !== 'all' && user.status !== statusFilter) return false
      return true
    })
    return byFilter.sort((a, b) => {
      const aName = (a.displayName || a.email || '').toLowerCase()
      const bName = (b.displayName || b.email || '').toLowerCase()
      if (aName && bName) return aName.localeCompare(bName)
      if (aName) return -1
      if (bName) return 1
      return 0
    })
  }, [users, roleFilter, statusFilter])

  const updateUser = (id: string, changes: Partial<AdminUser>, actionDescription: string) => {
    setUsers((current) => current.map((u) => (u.userId === id ? { ...u, ...changes } : u)))
    setLastAction(actionDescription)
    setTimeout(() => setLastAction(null), 4000)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage viewer, creator, and admin accounts from live admin user data.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-xs">
        <div className="inline-flex items-center gap-2">
          <span className="text-gray-400">Role:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {(['all', 'viewer', 'creator', 'admin'] as RoleFilter[]).map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => setRoleFilter(role)}
                className={`px-3 py-1 rounded-full capitalize ${
                  roleFilter === role ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-gray-400">Status:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {(['all', 'active', 'suspended'] as StatusFilter[]).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 rounded-full capitalize ${
                  statusFilter === status
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[minmax(0,1.8fr)_minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>User</div>
          <div>Email</div>
          <div>Role</div>
          <div className="text-right">Actions</div>
        </div>
        {filteredUsers.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No users match the selected filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {filteredUsers.map((user) => (
              <div
                key={user.userId}
                className="grid grid-cols-[minmax(0,1.8fr)_minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-3 text-sm items-center"
              >
                <div>
                  <Link
                    to={`/admin/users/${user.userId}`}
                    className="font-medium text-white hover:underline"
                    title="View user details"
                  >
                    {user.displayName || user.email}
                  </Link>
                  <p className="text-xs text-gray-500 capitalize">
                    Status: {user.status || 'unknown'}
                  </p>
                </div>
                <div className="text-gray-300 truncate">{user.email}</div>
                <div className="text-xs capitalize text-gray-300">{user.role}</div>
                <div className="flex justify-end gap-2">
                  {user.role !== 'admin' && (
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-7 w-7 border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                      onClick={async () => {
                        await promoteUserToAdmin(user.userId)
                        updateUser(
                          user.userId,
                          { role: 'admin', isAdmin: true },
                          `Promoted ${user.email} to admin`,
                        )
                      }}
                      title="Promote to admin (stubbed API)"
                    >
                      <Crown className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-7 w-7 border-gray-800 text-gray-300 hover:bg-white/5"
                    onClick={() =>
                      updateUser(
                        user.userId,
                        {},
                        `Password reset link sent to ${user.email} (mock only)`,
                      )
                    }
                    title="Send password reset link (mock only)"
                  >
                    <Mail className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    className={`h-7 w-7 ${
                      user.status === 'suspended'
                        ? 'border-emerald-700 text-emerald-400 hover:bg-emerald-900/20'
                        : 'border-red-800 text-red-400 hover:bg-red-900/20'
                    }`}
                    onClick={() =>
                      updateUser(
                        user.userId,
                        { status: user.status === 'suspended' ? 'active' : 'suspended' },
                        user.status === 'suspended'
                          ? `Unsuspended ${user.email}`
                          : `Suspended ${user.email}`,
                      )
                    }
                    title={
                      user.status === 'suspended'
                        ? 'Unsuspend user (mock only)'
                        : 'Suspend user (mock only)'
                    }
                  >
                    {user.status === 'suspended' ? (
                      <PlayCircle className="h-3.5 w-3.5" />
                    ) : (
                      <PauseCircle className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lastAction && (
        <div className="text-[11px] text-gray-400">
          <span className="font-semibold text-[#D4AF37] mr-1">Note:</span>
          {lastAction}. This is a UI stub only; once admin APIs are available, these actions should
          call real endpoints.
        </div>
      )}
    </div>
  )
}

